import { useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import { FormField } from "components/form/FormField/FormField";
import { Checkbox } from "components/ui/Checkbox";
import { InfoTooltip } from "components/ui/InfoTooltip";
import {
  HBA1C_OUT_OF_RANGE_WARNING,
  NT_PROBNP_ALERT,
  OCF_PP_NT_PROBNP_ENABLED,
} from "features/cdm/constants/formOptions";
import {
  isLabValueOutOfRange,
  withLabRange,
} from "features/cdm/utils/withLabRange";
import { onInvalidSubmit } from "utils/reportFormErrors";

import { ItemFormWrapper } from "../../../../../components/ItemFormWrapper/ItemFormWrapper";
import style from "./TestResultsForm.module.css";

const ACR_INFO =
  'For investigation of HTN/chronic kidney disease at OCF or PP visit. If result is abnormal, this test can be repeated "if clinically indicated" at next review.';

// Ref 33a: Non-HDL Cholesterol is auto-calculated as Total Cholesterol − HDL.
// Sentinel strings (Not Available) parse to NaN, so the field clears
// automatically when either input is non-numeric.
const computeNonHdl = (total, hdl) => {
  const totalValue = parseFloat(total);
  const hdlValue = parseFloat(hdl);

  if (isNaN(totalValue) || isNaN(hdlValue)) return "";

  const nonHdl = totalValue - hdlValue;

  // Non-HDL is clinically impossible below zero (Total < HDL). Blank the field
  // rather than show a negative value; the Total < HDL inconsistency is flagged
  // as a validation error on the HDL field (see superRefine).
  return nonHdl < 0 ? "" : nonHdl.toFixed(1);
};

export const TestResultsForm = ({
  onBack,
  onContinue,
  data,
  isReview = false,
}) => {
  const formSchema = z.object({
    // Mandatory on OCF per the Investigations Matrix (CDM 4 v1.2). LDL and eGFR
    // accept the "Not Available" sentinel, which satisfies the required check.
    haemoglobin: withLabRange(
      z.string().min(1, "Haemoglobin is required"),
      "haemoglobin",
      "Haemoglobin"
    ),
    totalCholesterol: withLabRange(
      z.string().min(1, "Total cholesterol is required"),
      "totalCholesterol",
      "Total cholesterol"
    ),
    hdlCholesterol: withLabRange(
      z.string().min(1, "HDL cholesterol is required"),
      "hdlCholesterol",
      "HDL cholesterol"
    ),
    // Auto-calculated (disabled) — derived from the others, so not required here.
    nonHdlCholesterol: z.string().optional(),
    ldlCholesterol: withLabRange(
      z.string().min(1, "LDL cholesterol is required"),
      "ldlCholesterol",
      "LDL cholesterol"
    ),
    triglycerides: withLabRange(
      z.string().min(1, "Triglycerides is required"),
      "triglycerides",
      "Triglycerides"
    ),
    // HbA1c is NOT range-blocked: an out-of-range value (outside 28–150
    // mmol/mol) is submitted as entered and recorded as 0 by the backend per the
    // HL7 spec. An inline warning is shown below the field instead of blocking.
    hba1c: z.string().min(1, "HbA1c is required"),
    serumCreatinine: withLabRange(
      z.string().min(1, "Serum creatinine is required"),
      "serumCreatinine",
      "Serum creatinine"
    ),
    egfr: withLabRange(z.string().min(1, "eGFR is required"), "egfr", "eGFR"),
    // Required value-or-NA: enter a result or tick "Not available" (sentinel
    // "Not Available"), which the HL7 spec permits for ACR.
    acr: withLabRange(z.string().min(1, "ACR is required"), "acr", "ACR"),
    // NT-proBNP — hidden until lab capacity is increased (OCF_PP_NT_PROBNP_ENABLED)
    ntProBNP: withLabRange(z.string().optional(), "ntProBNP", "NT-ProBNP"),
  }).superRefine((values, ctx) => {
    // Non-HDL Cholesterol (Total − HDL) cannot be negative: Total Cholesterol
    // must be at least HDL Cholesterol. Sentinels ("Not Available") parse to NaN
    // and are skipped.
    const totalValue = parseFloat(values.totalCholesterol);
    const hdlValue = parseFloat(values.hdlCholesterol);

    if (!isNaN(totalValue) && !isNaN(hdlValue) && totalValue < hdlValue) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Total Cholesterol cannot be less than HDL Cholesterol",
        path: ["hdlCholesterol"],
      });
    }
  });

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      haemoglobin: data?.haemoglobin || "",
      totalCholesterol: data?.totalCholesterol || "",
      hdlCholesterol: data?.hdlCholesterol || data?.tcHdlRatio || "",
      nonHdlCholesterol: data?.nonHdlCholesterol || "",
      ldlCholesterol: data?.ldlCholesterol || "",
      triglycerides: data?.triglycerides || "",
      hba1c: data?.hba1c || "",
      serumCreatinine: data?.serumCreatinine || "",
      egfr: data?.egfr || "",
      acr: data?.acr || "",
      ntProBNP: data?.ntProBNP || "",
    },
  });

  const watchedTotalCholesterol = form.watch("totalCholesterol");
  const watchedHdlCholesterol = form.watch("hdlCholesterol");
  const isHba1cOutOfRange = isLabValueOutOfRange("hba1c", form.watch("hba1c"));

  useEffect(() => {
    form.setValue(
      "nonHdlCholesterol",
      computeNonHdl(watchedTotalCholesterol, watchedHdlCholesterol),
      { shouldValidate: true }
    );
  }, [watchedTotalCholesterol, watchedHdlCholesterol, form]);

  const handleFormSubmit = (values) => {
    onContinue(values);
  };

  return (
    <ItemFormWrapper
      onBack={onBack}
      onContinue={form.handleSubmit(handleFormSubmit, onInvalidSubmit("TestResultsForm"))}
      isReview={isReview}
    >
      <div
        className={style.itemWrapper}
        style={{
          marginBottom: "0px",
          paddingBottom: "0px",
          borderBottom: "none",
        }}
      >
        <FormProvider {...form}>
          <form
            className={style.addReferralForm}
            onSubmit={form.handleSubmit(handleFormSubmit, onInvalidSubmit("TestResultsForm"))}
          >
            <div className={style.itemWrapper}>
              <h3>Investigations</h3>
              <div
                className={style.inputGroupGrid3Columns}
                style={{ marginTop: "16px" }}
              >
                <FormField name="haemoglobin">
                  <label className={style.addReferralFormLabel}>
                    Haemoglobin (FBC)
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    inputMode="numeric"
                    onInput={(e) =>
                      (e.target.value = e.target.value.replace(/[^0-9.]/g, ""))
                    }
                    disabled={isReview}
                    {...form.register("haemoglobin")}
                  />
                </FormField>
                <FormField name="totalCholesterol">
                  <label className={style.addReferralFormLabel}>
                    Total Cholesterol (Lipids)
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    inputMode="numeric"
                    onInput={(e) =>
                      (e.target.value = e.target.value.replace(/[^0-9.]/g, ""))
                    }
                    disabled={isReview}
                    {...form.register("totalCholesterol")}
                  />
                </FormField>
                <FormField name="hdlCholesterol">
                  <label className={style.addReferralFormLabel}>
                    HDL Cholesterol (Lipids)
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    inputMode="numeric"
                    onInput={(e) =>
                      (e.target.value = e.target.value.replace(/[^0-9.]/g, ""))
                    }
                    disabled={isReview}
                    {...form.register("hdlCholesterol")}
                  />
                </FormField>
                <FormField name="nonHdlCholesterol">
                  <label className={style.addReferralFormLabel}>
                    Non-HDL Cholesterol (Lipids)
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    disabled
                    value={form.watch("nonHdlCholesterol") ?? ""}
                  />
                </FormField>
                <FormField name="ldlCholesterol">
                  <label className={style.addReferralFormLabel}>
                    LDL Cholesterol (Lipids)
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    disabled={isReview || form.watch("ldlCholesterol") === "Not Available"}
                    value={
                      form.watch("ldlCholesterol") === "Not Available"
                        ? "Not available"
                        : form.watch("ldlCholesterol") ?? ""
                    }
                    onChange={(e) =>
                      form.setValue("ldlCholesterol", e.target.value, {
                        shouldValidate: true,
                      })
                    }
                  />
                  <label className={style.naCheckbox}>
                    <Checkbox
                      checked={form.watch("ldlCholesterol") === "Not Available"}
                      disabled={isReview}
                      onChange={(e) =>
                        form.setValue(
                          "ldlCholesterol",
                          e.target.checked ? "Not Available" : "",
                          { shouldValidate: true }
                        )
                      }
                    />
                    Not available
                  </label>
                </FormField>
              </div>
              <div
                className={style.inputGroupGrid3Columns}
                style={{ marginTop: "16px" }}
              >
                <FormField name="triglycerides">
                  <label className={style.addReferralFormLabel}>
                    Triglycerides (Lipids)
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    inputMode="numeric"
                    onInput={(e) =>
                      (e.target.value = e.target.value.replace(/[^0-9.]/g, ""))
                    }
                    disabled={isReview}
                    {...form.register("triglycerides")}
                  />
                </FormField>
                <FormField name="hba1c">
                  <label className={style.addReferralFormLabel}>HbA1c</label>
                  <div className={style.mmolInput}>
                    <input
                      type="text"
                      className="form-control"
                      inputMode="numeric"
                      onInput={(e) =>
                        (e.target.value = e.target.value.replace(/[^0-9.]/g, ""))
                      }
                      disabled={isReview}
                      {...form.register("hba1c")}
                    />
                  </div>
                  {isHba1cOutOfRange && (
                    <p className={style.hba1cWarning}>
                      {HBA1C_OUT_OF_RANGE_WARNING}
                    </p>
                  )}
                </FormField>
                <FormField name="serumCreatinine">
                  <label className={style.addReferralFormLabel}>
                    Serum Creatinine
                  </label>

                  <input
                    type="text"
                    className="form-control"
                    inputMode="numeric"
                    onInput={(e) =>
                      (e.target.value = e.target.value.replace(/[^0-9.]/g, ""))
                    }
                    disabled={isReview}
                    {...form.register("serumCreatinine")}
                  />
                </FormField>
              </div>
              <div
                className={style.inputGroupGrid3Columns}
                style={{ marginTop: "16px" }}
              >
                <FormField name="egfr">
                  <label className={style.addReferralFormLabel}>
                    eGFR (Urea & Electrolytes)
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    disabled={isReview || form.watch("egfr") === "Not Available"}
                    value={
                      form.watch("egfr") === "Not Available"
                        ? "Not available"
                        : form.watch("egfr") ?? ""
                    }
                    onChange={(e) =>
                      form.setValue("egfr", e.target.value, {
                        shouldValidate: true,
                      })
                    }
                  />
                  <label className={style.naCheckbox}>
                    <Checkbox
                      checked={form.watch("egfr") === "Not Available"}
                      disabled={isReview}
                      onChange={(e) =>
                        form.setValue(
                          "egfr",
                          e.target.checked ? "Not Available" : "",
                          { shouldValidate: true }
                        )
                      }
                    />
                    Not available
                  </label>
                </FormField>
                <FormField name="acr">
                  <label className={style.addReferralFormLabel}>
                    Albumin/Creatinine Ratio (ACR)
                    <InfoTooltip text={ACR_INFO} />
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    inputMode="numeric"
                    disabled={isReview || form.watch("acr") === "Not Available"}
                    value={
                      form.watch("acr") === "Not Available"
                        ? "Not available"
                        : form.watch("acr") ?? ""
                    }
                    onChange={(e) =>
                      form.setValue("acr", e.target.value.replace(/[^0-9.]/g, ""), {
                        shouldValidate: true,
                      })
                    }
                  />
                  <label className={style.naCheckbox}>
                    <Checkbox
                      checked={form.watch("acr") === "Not Available"}
                      disabled={isReview}
                      onChange={(e) =>
                        form.setValue(
                          "acr",
                          e.target.checked ? "Not Available" : "",
                          { shouldValidate: true }
                        )
                      }
                    />
                    Not available
                  </label>
                  <p className={style.fieldInfo}>
                    For investigation of HTN / chronic kidney disease at the OCF
                    or PP visit. If the result is abnormal, this test can be
                    repeated &quot;if clinically indicated&quot; at the next
                    review.
                  </p>
                </FormField>
                {!!OCF_PP_NT_PROBNP_ENABLED && (
                  <FormField name="ntProBNP">
                    <label className={style.addReferralFormLabel}>
                      NT-proBNP
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      inputMode="numeric"
                      disabled={
                        isReview || form.watch("ntProBNP") === "Not Available"
                      }
                      value={
                        form.watch("ntProBNP") === "Not Available"
                          ? "Not available"
                          : form.watch("ntProBNP") ?? ""
                      }
                      onChange={(e) =>
                        form.setValue(
                          "ntProBNP",
                          e.target.value.replace(/[^0-9.]/g, ""),
                          { shouldValidate: true }
                        )
                      }
                    />
                    <label className={style.naCheckbox}>
                      <Checkbox
                        checked={form.watch("ntProBNP") === "Not Available"}
                        disabled={isReview}
                        onChange={(e) =>
                          form.setValue(
                            "ntProBNP",
                            e.target.checked ? "Not Available" : "",
                            { shouldValidate: true }
                          )
                        }
                      />
                      Not available
                    </label>
                  </FormField>
                )}
              </div>
              {/* NEW 19-03-2024 (CDM dataset V3.1, row 63b): the NTproBNP
                  indications alert must show on EVERY review across Treatment,
                  PP & OCF — rendered independently of the NT-proBNP field
                  (gated off in OCF/PP by OCF_PP_NT_PROBNP_ENABLED). Placed
                  under the test-results grid as a full-width note, not tied to
                  a single column. */}
              <p className={style.ntProBnpAlert}>{NT_PROBNP_ALERT}</p>
            </div>
          </form>
        </FormProvider>
      </div>
    </ItemFormWrapper>
  );
};
